import React, { useState, useCallback, useRef, useEffect, JSX } from 'react';
import { render } from 'storyblok-rich-text-react-renderer';
import styles from './RecommendedOptions.module.scss';
import { ComponentWithContentProps } from '../types';
import {
  fetchOptions,
  fetchStoryblokOptions,
  updateCartWithServiceOption,
  createServiceOptionPayload,
  getTranslation
} from './RecommendedOptionsUtils';
import Row from '../ResponsiveRow/row';
import ServiceOptionCard from './ServiceOption/ServiceOptionCard/ServiceOptionCard';

import ReactInputSelectV2 from '../ProductDetailsV3/ReactInputSelectV2/ReactInputSelectV2';
import ServiceOptionLoader from './ServiceOption/ServiceOptionLoader';
import type {
  NormalizedServiceOptionsPayload,
  StoryblokReferenceBlock,
  FilterOptionPair,
  DropdownFilterOption,
  FilterCategoryConfigBlock,
  RecommendedOptionsTabItem,
  RecommendedOptionsSchema,
  RecommendedOptionsCallbacks,
  BaseProductWithCodes,
  CategorySection
} from './types';
import { getCookie } from 'cookies-next';
import {
  INTERNET_SERVICE_OPTION,
  MOBILEPOSTPAID_SERVICE_OPTION,
  TV_SERVICE_OPTION,
  VOICE_SERVICE_OPTION
} from '../../utils/constants';
import { viewport } from '../../utils/utils';
import FalconButtonV2 from '../FalconButtonV2/FalconButtonV2';
import { trackNewDatalayerEvent } from '../../utils/AnalyticsUtils';
import {
  BRAND,
  CURRENCY,
  GAEVENT,
  APPLYFILTER,
  VIEWITEM,
  TABCLICK,
  ADDOPTIONTOCART,
  REMOVEOPTIONFROMCART
} from '../../Analytics/AnalyticsConstants';
import { itemInfoEvent } from '../../Analytics/items';
const cartId = getCookie('one-commerce-cartId');

const normalizeHeight = (value: string | number | boolean | undefined): string | undefined => {
  if (!value || typeof value === 'boolean') return undefined;

  const stringValue = String(value).trim();

  // Check if it's a CSS expression (calc, var, min, max, clamp, etc.)
  if (stringValue.match(/^(calc|var|min|max|clamp)\(/i)) {
    return stringValue;
  }

  // Check if it's already a CSS unit (px, vh, vw, rem, em, %, etc.)
  if (stringValue.match(/\d+(px|vh|vw|rem|em|%|vmin|vmax)$/i)) {
    return stringValue;
  }

  // Try to parse as number and add px
  const numValue = parseFloat(stringValue);
  if (!isNaN(numValue) && numValue > 0) {
    return `${numValue}px`;
  }

  return undefined;
};

const getCategoryCodesFromProduct = (product: any): string[] => {
  if (!product || typeof product !== 'object') {
    return [];
  }

  const productContent = product.content ?? {};
  const rawCategory = productContent?.category;

  if (!rawCategory) {
    return [];
  }

  const categories = Array.isArray(rawCategory) ? rawCategory : [rawCategory];

  return categories
    .map((categoryItem: any) => {
      if (typeof categoryItem === 'string') {
        return categoryItem.trim();
      }

      if (categoryItem && typeof categoryItem === 'object') {
        const codeCandidate = (
          categoryItem.code ??
          categoryItem.value ??
          categoryItem.name ??
          ''
        ).toString();

        return codeCandidate.trim();
      }

      return '';
    })
    .filter(Boolean);
};

const isFilterCategoryConfigBlock = (entry: unknown): entry is FilterCategoryConfigBlock => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }

  return (entry as FilterCategoryConfigBlock).component === 'filterCategoryConfig';
};

const extractFilterOptionPairs = (tab?: RecommendedOptionsTabItem | null): FilterOptionPair[] => {
  if (!tab) {
    return [];
  }

  const rawFilterOptions = tab.filterOptions;
  const collectBodies = (block: StoryblokReferenceBlock | undefined): unknown[] => {
    if (!block) {
      return [];
    }

    const body = block.reference?.content?.body;
    return Array.isArray(body) ? body : [];
  };

  const nodes: unknown[] = Array.isArray(rawFilterOptions)
    ? rawFilterOptions.flatMap((block) => collectBodies(block))
    : collectBodies(rawFilterOptions);

  return nodes
    .filter(isFilterCategoryConfigBlock)
    .map((item) => ({
      value: String(item.value ?? '').trim(),
      name: String(item.name ?? '').trim()
    }))
    .filter(({ value, name }) => Boolean(value) && Boolean(name));
};

const areFilterOptionPairsEqual = (prev: FilterOptionPair[], next: FilterOptionPair[]): boolean => {
  if (prev.length !== next.length) {
    return false;
  }

  return prev.every((item, index) => {
    const candidate = next[index];
    return item.value === candidate.value && item.name === candidate.name;
  });
};

const normalizeOptionsResponse = (response: unknown): NormalizedServiceOptionsPayload => {
  if (Array.isArray(response)) {
    return {
      options: response,
      raw: response
    };
  }

  if (response && typeof response === 'object') {
    const candidates = [
      'options',
      'data',
      'result',
      'serviceOptions',
      'items',
      'conflicts'
    ] as const;
    const candidateArray = candidates
      .map((key) => (response as Record<string, unknown>)[key])
      .find((value): value is unknown[] => Array.isArray(value));

    return {
      options: candidateArray ?? [],
      raw: response
    };
  }

  return {
    options: [],
    raw: response
  };
};

type ServiceOptionPayloadArgs = {
  id: string;
  isSelected: boolean;
  quantity: number;
  offer: any;
  itemType?: string;
};

// Using cache for service options to avoid refetching when switching tabs
const serviceOptionsCache: Record<string, NormalizedServiceOptionsPayload> = {};
const storyblokOptionsCache: Record<string, any> = {};

// Main component using Storyblok pattern
const RecommendedOptions: React.FC<
  ComponentWithContentProps<RecommendedOptionsSchema> &
    RecommendedOptionsCallbacks & {
      cartItem?: any;
      activelocaleId?: string;
      updateCartData?: (cartData: unknown) => void;
      isPartialFetching?: boolean;
      onSuccess?: () => void;
    }
> = ({
  content,
  metadata,
  className,
  'aria-label': ariaLabel,
  cartItem,
  activelocaleId,
  updateCartData,
  isPartialFetching,
  onSuccess
}) => {
  const viewPort = viewport();
  const {
    tabs: incomingTabs = [],
    headline = '',
    description = [],
    translations = []
  } = content || {};
  const cartItemTypeSet = new Set(
    Array.isArray(cartItem?.items)
      ? cartItem.items
          .map((it: any) => (typeof it?.itemType === 'string' ? it.itemType.trim() : ''))
          .filter(Boolean)
      : []
  );

  //Only list tab if both present on storyblok and on cart
  const tabs = Array.isArray(incomingTabs)
    ? (incomingTabs as RecommendedOptionsTabItem[]).filter(
        (t) => typeof t?.tabKey === 'string' && cartItemTypeSet.has(t.tabKey)
      )
    : [];

  const serviceOptionItemTypes = React.useMemo(
    () =>
      new Set([
        TV_SERVICE_OPTION,
        INTERNET_SERVICE_OPTION,
        VOICE_SERVICE_OPTION,
        MOBILEPOSTPAID_SERVICE_OPTION
      ]),
    []
  );

  const serviceOptionsOnCart = React.useMemo(() => {
    if (!Array.isArray(cartItem?.items)) {
      return [] as any[];
    }

    return cartItem.items.filter((item: any) =>
      serviceOptionItemTypes.has(typeof item?.itemType === 'string' ? item.itemType : '')
    );
  }, [cartItem?.items, serviceOptionItemTypes]);

  const [activeTabKey, setActiveTabKey] = useState<string>(tabs[0]?.tabKey || '');
  const [activeTabFilterOptions, setActiveTabFilterOptions] = useState<FilterOptionPair[]>([]);
  const [activeFilterValue, setActiveFilterValue] = useState<string>('ALL');
  const [serviceOptionsData, setServiceOptionsData] = useState<
    Record<string, NormalizedServiceOptionsPayload>
  >(() => ({ ...serviceOptionsCache }));
  const [loadingTabs, setLoadingTabs] = useState<Record<string, boolean>>({});
  const storyblokCacheKey = [activelocaleId, content?.optionCatalogPath].filter(Boolean).join('|');
  const [storyblokOptions, setStoryblokOptions] = useState<any>(
    () => storyblokOptionsCache[storyblokCacheKey] ?? null
  );
  const [storyblokOptionsLoading, setStoryblokOptionsLoading] = useState<boolean>(false);
  const [storyblokOptionsError, setStoryblokOptionsError] = useState<any>(null);
  const [beneficiaryProducts, setBeneficiaryProducts] = useState<any[]>([]);
  const [cartLoading, setCartLoading] = useState<boolean>(false);
  const [hasTrackedViewItemList, setHasTrackedViewItemList] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [multipleSelections, setMultipleSelections] = useState<ServiceOptionPayloadArgs[]>([]);
  const allowMultipleOptionsSelection = content?.allowMultipleOptionsSelection ?? false;

  const isProductInServiceOptions = React.useCallback(
    (productId: string | null | undefined): boolean => {
      if (!productId) {
        return false;
      }

      if (allowMultipleOptionsSelection) {
        // Check if product is in multiple selections
        const selectionItem = multipleSelections.find((item) => item.id === productId);
        if (selectionItem) {
          // If in multipleSelections with quantity 0, it's marked for removal
          return selectionItem.quantity > 0;
        }

        // Check if product is in cart
        const isInCart = serviceOptionsOnCart.some((item: any) => item?.id === productId);
        if (!isInCart) {
          return false;
        }

        // Product is in cart, but check if any selected item conflicts with it
        const hasConflict = multipleSelections.some((selected) => {
          const conflicts = selected.offer?.conflicts || [];
          return conflicts.includes(productId);
        });

        // If there's a conflict, the product is considered "not selected" (will be removed)
        return !hasConflict;
      }

      if (!serviceOptionsOnCart?.length) {
        return false;
      }

      return serviceOptionsOnCart.some((item: any) => item?.id === productId);
    },
    [serviceOptionsOnCart, multipleSelections, allowMultipleOptionsSelection]
  );

  const buildServiceOptionPayload = ({
    id,
    isSelected,
    quantity,
    offer
  }: ServiceOptionPayloadArgs) => {
    const conflicts = offer?.conflicts || [];
    let payload: Record<string, number> = {};

    if (!isSelected) {
      payload = {
        [id]: 0
      };
    } else {
      payload = {
        [id]: quantity
      };
    }
    //searching conflicts and if available they match serviceOptionsOnCart id add to payload with 0
    for (const conflictOption of conflicts) {
      const matchingItem = serviceOptionsOnCart.find((item: any) => item?.id === conflictOption);
      if (matchingItem) {
        payload[conflictOption] = 0;
      }
    }

    return payload;
  };

  const buildMultipleServiceOptionPayload = (selections: ServiceOptionPayloadArgs[]) => {
    // Group selections by itemType
    const selectionsByItemType = selections.reduce(
      (acc, args) => {
        const itemType = args.itemType || '';
        if (!acc[itemType]) {
          acc[itemType] = [];
        }
        acc[itemType].push(args);
        return acc;
      },
      {} as Record<string, ServiceOptionPayloadArgs[]>
    );

    // Build cartItem array with all itemTypes
    const cartItemArray = Object.entries(selectionsByItemType).map(([itemType, selections]) => {
      // Build items map for this itemType
      const itemsMap: Record<string, number> = {};

      selections.forEach((args) => {
        const { id, quantity, offer } = args;
        const conflicts = offer?.conflicts || [];

        // Add the selected item
        itemsMap[id] = quantity;

        // Add conflicts with count 0
        conflicts.forEach((conflictId: string) => {
          const matchingItem = serviceOptionsOnCart.find((item: any) => item?.id === conflictId);
          if (matchingItem) {
            itemsMap[conflictId] = 0;
          }
        });
      });

      // Convert to items array format
      const items = Object.entries(itemsMap).map(([id, count]) => ({
        id,
        count
      }));

      return {
        itemType,
        items
      };
    });

    return {
      cartItem: cartItemArray
    };
  };

  const handleMultiSelection = (payloadArgs: ServiceOptionPayloadArgs) => {
    if (!allowMultipleOptionsSelection) return;

    const { id, offer, quantity } = payloadArgs;
    const conflicts = offer?.conflicts || [];

    // Check if item is in multipleSelections array
    const isInMultipleSelections = multipleSelections.some((selected) => selected.id === id);

    // Check if item is in cart
    const isInCart = serviceOptionsOnCart.some((item: any) => item?.id === id);

    let updatedSelections: ServiceOptionPayloadArgs[];

    // If currently selected (either in multipleSelections or in cart)
    if (isInMultipleSelections || (isInCart && !isInMultipleSelections)) {
      // Special handling for 'HZN TV MultiBox' to allow quantity updates without deselection
      if (id === 'HZN TV MultiBox' && quantity > 0) {
        if (isInMultipleSelections) {
          updatedSelections = multipleSelections.map((selectedOption) =>
            selectedOption.id === id ? { ...selectedOption, quantity } : selectedOption
          );
        } else {
          // Item is in cart, add to selections with updated quantity
          updatedSelections = [...multipleSelections, payloadArgs];
        }
      } else {
        if (isInMultipleSelections) {
          // Remove from multipleSelections
          updatedSelections = multipleSelections.filter((selected) => selected.id !== id);
        } else {
          // Item is in cart but not in selections, add it with quantity 0 to mark for removal
          updatedSelections = [
            ...multipleSelections,
            { ...payloadArgs, isSelected: false, quantity: 0 }
          ];
        }
      }
    } else {
      // Remove any conflicting options before adding new selection
      updatedSelections = multipleSelections.filter((selected) => !conflicts.includes(selected.id));
      // Add new selection
      updatedSelections = [...updatedSelections, payloadArgs];
    }

    setMultipleSelections(updatedSelections);
  };

  const emitServiceOptionSelection = React.useCallback(
    async (payloadArgs: ServiceOptionPayloadArgs | ServiceOptionPayloadArgs[]) => {
      const updateCartUsecase =
        cartItem?.itemType === 'MOBILE_ORDER' ? 'MOBILE_SERVICE_OPTION' : undefined;

      if (allowMultipleOptionsSelection && Array.isArray(payloadArgs)) {
        if (typeof cartId === 'string' && cartId) {
          setCartLoading(true);
          try {
            const requestPayload = buildMultipleServiceOptionPayload(payloadArgs);
            const cartData = await updateCartWithServiceOption(
              cartId,
              requestPayload,
              updateCartUsecase
            );

            // Track analytics for all options
            payloadArgs.forEach((args, index) => {
              trackOptionCartEvent(args.offer, index, false);
            });

            if (typeof updateCartData === 'function') {
              updateCartData(cartData);
            }

            // call additional parent action if needed, e.g., to close a modal or show a confirmation message
            if (typeof onSuccess === 'function') {
              onSuccess();
            }

            // Clear selections after successful submission
            setMultipleSelections([]);
          } finally {
            setCartLoading(false);
          }
        }
        return;
      }

      // Single selection logic (existing code)
      if (!Array.isArray(payloadArgs)) {
        const payload = buildServiceOptionPayload(payloadArgs);

        const itemTypeMap: Record<string, string> = {
          TV: TV_SERVICE_OPTION,
          INTERNET: INTERNET_SERVICE_OPTION,
          VOICE: VOICE_SERVICE_OPTION,
          MOBILEPOSTPAID_RATEPLAN: MOBILEPOSTPAID_SERVICE_OPTION
        };

        const itemType = itemTypeMap[activeTabKey];

        if (!itemType) return;

        if (typeof cartId === 'string' && cartId && payload) {
          setCartLoading(true);
          try {
            const requestPayload = createServiceOptionPayload(payload, itemType);
            const cartData = await updateCartWithServiceOption(
              cartId,
              requestPayload,
              updateCartUsecase
            );
            trackOptionCartEvent(payloadArgs?.offer, 0, !payloadArgs.isSelected);
            if (typeof updateCartData === 'function') {
              updateCartData(cartData);
            }
          } finally {
            setCartLoading(false);
          }
        }
      }
    },
    [
      buildServiceOptionPayload,
      buildMultipleServiceOptionPayload,
      cartItem?.itemType,
      updateCartData
    ]
  );

  const trackOptionCartEvent = (option: any, index: number, isRemoving: boolean) => {
    if (!option) return;

    const derivedPrices = option?.derivedPrice?.filter(
      (price: any) => price?.billingEvent == 'monthly'
    );
    const derivedPrice = derivedPrices?.[0]?.value;
    const monthlyPrices = option?.prices?.filter((price: any) => price?.billingEvent == 'monthly');
    const recurringPrice = monthlyPrices?.[0]?.value || '0';
    const finalPrice =
      derivedPrice && recurringPrice
        ? Math.min(parseFloat(derivedPrice), parseFloat(recurringPrice))
        : parseFloat(recurringPrice || '0');

    const itemData: itemInfoEvent = {
      item_id: option?.id || '',
      item_name: option?.id || '',
      affiliation: '',
      coupon: '',
      discount: 0,
      index: index,
      item_brand: BRAND,
      item_category: 'recommended',
      item_category2: 'options',
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_list_id: 'recommended_options',
      item_list_name: 'Recommended Options',
      item_variant: '',
      location_id: '',
      price: finalPrice,
      quantity: option?.count || 1,
      promotion_id: ''
    };

    const eventName = isRemoving ? REMOVEOPTIONFROMCART : ADDOPTIONTOCART;

    trackNewDatalayerEvent(GAEVENT, eventName, {
      transaction_type: 'Recommended Options',
      ecommerce: {
        currency: CURRENCY,
        value: finalPrice,
        items: [itemData]
      }
    });
  };

  useEffect(() => {
    // Set initial active tab to first tab if not set

    if (!tabs.length) {
      setActiveTabFilterOptions((prev) => (prev.length ? [] : prev));
      return;
    }

    if (!activeTabKey && tabs.length > 0) {
      const [firstTab] = tabs;
      if (firstTab?.tabKey) {
        setActiveTabKey(firstTab.tabKey);
      }
      const initialFilters = extractFilterOptionPairs(firstTab);
      setActiveTabFilterOptions((prev) =>
        areFilterOptionPairsEqual(prev, initialFilters) ? prev : initialFilters
      );
      return;
    }

    const currentTab = tabs.find((tab) => tab.tabKey === activeTabKey) ?? null;
    const nextFilters = extractFilterOptionPairs(currentTab);
    setActiveTabFilterOptions((prev) =>
      areFilterOptionPairsEqual(prev, nextFilters) ? prev : nextFilters
    );
  }, [tabs, activeTabKey]);

  useEffect(() => {
    if (typeof isPartialFetching === 'boolean') {
      setCartLoading(isPartialFetching);
    }
  }, [isPartialFetching]);

  const getProdNameForTab = useCallback(
    (tabKey: string): string => {
      if (!cartItem?.items) {
        return '';
      }

      const matchingItem = cartItem.items.find((item: any) => item?.itemType === tabKey);
      const prodNameRaw = matchingItem?.id ?? matchingItem?.offerId ?? '';

      if (typeof prodNameRaw !== 'string' || prodNameRaw.trim().length === 0) {
        return '';
      }

      const prodName = prodNameRaw.trim().replace(/\s+/g, ' ');
      return prodName;
    },
    [cartItem]
  );

  const loadOptionsForTab = useCallback(
    async (tabKey: string, retry?: boolean) => {
      if (!tabKey) {
        return;
      }

      // if (retry) {
      //   delete serviceOptionsData[tabKey];
      // }

      if (serviceOptionsData[tabKey] || loadingTabs[tabKey]) {
        return;
      }

      const prodName = getProdNameForTab(tabKey);
      if (!prodName) {
        return;
      }

      setLoadingTabs((prev) => ({ ...prev, [tabKey]: true }));

      try {
        const URL = content?.segmentTypes
          ? `${prodName}&segmentTypes=${content?.segmentTypes}`
          : prodName;
        const response = await fetchOptions(URL);
        const normalized = normalizeOptionsResponse(response);

        serviceOptionsCache[tabKey] = normalized;
        setServiceOptionsData((prev) => ({
          ...prev,
          [tabKey]: normalized
        }));
      } catch (error) {
        console.error('RecommendedOptions: Failed to fetch options', { tabKey, error });
        //setstate for new state just for tracking tabkey error
        setServiceOptionsData((prev) => ({
          ...prev,
          [tabKey]: {
            ...prev[tabKey],
            error: true
          }
        }));
      } finally {
        setLoadingTabs((prev) => {
          const { [tabKey]: _discard, ...rest } = prev;
          return rest;
        });
      }
    },
    [serviceOptionsData, loadingTabs, getProdNameForTab]
  );

  useEffect(() => {
    let newBeneficiaryProducts: any[] = [];

    Object.entries(serviceOptionsData).forEach(([key, data]) => {
      const options = data?.options;
      if (Array.isArray(options)) {
        const activeBeneficiaryInfos = options
          .filter((option: any) => {
            const hasBeneficiary =
              option?.beneficiaryProduct &&
              Array.isArray(option.beneficiaryProduct) &&
              option.beneficiaryProduct.length > 0;

            if (!hasBeneficiary) return false;

            return serviceOptionsOnCart.some((item: any) => item.id === option.id);
          })
          .flatMap((option: any) => option.beneficiaryProduct);

        if (activeBeneficiaryInfos.length > 0) {
          newBeneficiaryProducts = activeBeneficiaryInfos;
        }
      }
    });

    setBeneficiaryProducts((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newBeneficiaryProducts)) {
        return prev;
      }
      return newBeneficiaryProducts;
    });
  }, [serviceOptionsData, serviceOptionsOnCart]);

  const handleTabClick = useCallback(
    (tabKey: string) => {
      const tabLabel = tabs.find(
        (tab: RecommendedOptionsTabItem) => tab.tabKey === tabKey
      )?.tabLabel;

      setActiveTabKey(tabKey);
      void loadOptionsForTab(tabKey);

      // Track tab switch with analytics
      try {
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push({
            event: GAEVENT,
            event_name: TABCLICK,
            click_text: tabLabel || tabKey
          });
        }
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }

      // Announce tab change for screen readers
      if (tabLabel) {
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = `Switched to ${tabLabel} tab`;
        document.body.appendChild(announcement);

        setTimeout(() => {
          document.body.removeChild(announcement);
        }, 1000);
      }
    },
    [tabs, loadOptionsForTab]
  );

  useEffect(() => {
    if (!activeTabKey) {
      return;
    }

    void loadOptionsForTab(activeTabKey);
  }, [activeTabKey, loadOptionsForTab]);

  const storyblokFetchArgs = React.useMemo(() => {
    if (
      !activelocaleId ||
      !content?.optionCatalogPath ||
      !Array.isArray(content?.categories) ||
      content.categories.length === 0
    ) {
      return null;
    }

    return {
      path: content.optionCatalogPath,
      categories: (content.categories as string[]).map((category) =>
        typeof category === 'string' ? category : String(category)
      ),
      locale: activelocaleId
    };
  }, [activelocaleId, content?.optionCatalogPath, content?.categories]);

  useEffect(() => {
    if (!storyblokFetchArgs) {
      setStoryblokOptions(null);
      return;
    }

    const cacheKey = [storyblokFetchArgs?.locale, storyblokFetchArgs?.path]
      .filter(Boolean)
      .join('|');

    if (storyblokOptionsCache[cacheKey]) {
      setStoryblokOptions(storyblokOptionsCache[cacheKey]);
      return;
    }

    let cancelled = false;
    setStoryblokOptionsLoading(true);
    setStoryblokOptionsError(false);

    const load = async () => {
      try {
        const response = await fetchStoryblokOptions(
          storyblokFetchArgs.path,
          storyblokFetchArgs.categories,
          storyblokFetchArgs.locale
        );

        if (!cancelled) {
          storyblokOptionsCache[cacheKey] = response;
          setStoryblokOptions(response);
        }
      } catch (err) {
        if (!cancelled) {
          setStoryblokOptions(null);
          setStoryblokOptionsError(true);
        }
      } finally {
        if (!cancelled) {
          setStoryblokOptionsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [storyblokFetchArgs]);

  const containerClasses = [styles['recommended-options'], className].filter(Boolean).join(' ');

  const activeOptions = serviceOptionsData[activeTabKey]?.options ?? [];

  const trackViewItemList = useCallback(() => {
    if (!activeOptions || activeOptions.length === 0 || hasTrackedViewItemList) {
      return;
    }

    const items: itemInfoEvent[] = activeOptions.map((option: any, index: number) => {
      const derivedPrices = option?.derivedPrice?.filter(
        (price: any) => price?.billingEvent == 'monthly'
      );
      const derivedPrice = derivedPrices?.[0]?.value;
      const monthlyPrices = option?.prices?.filter(
        (price: any) => price?.billingEvent == 'monthly'
      );
      const recurringPrice = monthlyPrices?.[0]?.value || '0';
      const finalPrice =
        derivedPrice && recurringPrice
          ? Math.min(parseFloat(derivedPrice), parseFloat(recurringPrice))
          : parseFloat(recurringPrice || '0');

      return {
        item_id: option?.id || '',
        item_name: option?.id || '',
        affiliation: '',
        coupon: '',
        discount: 0,
        index: index,
        item_brand: BRAND,
        item_category: 'recommended',
        item_category2: 'options',
        item_category3: '',
        item_category4: '',
        item_category5: '',
        item_list_id: 'recommended_options',
        item_list_name: 'Recommended Options',
        item_variant: '',
        location_id: '',
        price: finalPrice,
        quantity: 1,
        promotion_id: ''
      };
    });

    try {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: VIEWITEM,
          event_name: VIEWITEM,
          ecommerce: {
            item_list_id: 'recommended_options',
            item_list_name: 'Recommended Options',
            items: items
          }
        });
        setHasTrackedViewItemList(true);
      }
    } catch (error) {
      console.error('Analytics tracking error (view_item_list):', error);
    }
  }, [activeOptions, hasTrackedViewItemList]);

  // Intersection Observer to track when component is visible
  useEffect(() => {
    if (!containerRef.current || hasTrackedViewItemList || !activeOptions.length) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedViewItemList) {
            trackViewItemList();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when at least 10% of component is visible
        rootMargin: '0px'
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, hasTrackedViewItemList, activeOptions, trackViewItemList]);

  const baseFilteredOptions = React.useMemo<BaseProductWithCodes[]>(() => {
    if (!Array.isArray(storyblokOptions)) {
      return [];
    }

    const activeTab = tabs.find((t) => t.tabKey === activeTabKey);
    const tabOptionIds = new Set(
      (activeTab?.optionsList ?? []).map((id) => String(id).trim()).filter(Boolean)
    );

    const baseFiltered = storyblokOptions.filter((prod: any) => {
      const prodId = String(prod?.content?.code ?? '').trim();
      if (!prodId) return false;

      const matchesTab = tabOptionIds.size === 0 || tabOptionIds.has(prodId);
      if (!matchesTab) return false;

      const matchingApiOption = activeOptions.find((o: any) => String(o?.id).trim() === prodId);

      if (!matchingApiOption) return false;

      const sbCategories = getCategoryCodesFromProduct(prod);
      const apiCategories = Array.isArray(matchingApiOption.category)
        ? matchingApiOption.category
        : [];

      const hasCategoryMatch = sbCategories.some((sbCat) => apiCategories.includes(sbCat));

      return hasCategoryMatch;
    });

    return baseFiltered.map((prod: any) => ({
      raw: prod,
      codes: getCategoryCodesFromProduct(prod)
    }));
  }, [storyblokOptions, activeTabKey, tabs, activeOptions]);

  const dropdownFilterOptions = React.useMemo(() => {
    if (!activeTabFilterOptions.length) {
      return [] as DropdownFilterOption[];
    }

    const groups = new Map<string, { name: string; codes: Set<string>; order: number }>();
    let allOption: DropdownFilterOption | undefined;

    activeTabFilterOptions.forEach((option, index) => {
      const trimmedName = option.name?.trim();
      const normalizedValue = option.value?.trim();

      if (!trimmedName || !normalizedValue) {
        return;
      }

      if (normalizedValue.toUpperCase() === 'ALL') {
        const itemsLength = baseFilteredOptions.length;
        allOption = {
          value: 'ALL',
          name: trimmedName,
          label: `${trimmedName}`,
          itemsLength,
          codes: []
        };
        return;
      }

      const key = trimmedName.toLowerCase();
      const existing = groups.get(key);

      if (existing) {
        existing.codes.add(normalizedValue);
      } else {
        groups.set(key, {
          name: trimmedName,
          codes: new Set([normalizedValue]),
          order: index
        });
      }
    });

    const computeItemsLength = (codes: string[]): number => {
      if (!codes.length) {
        return baseFilteredOptions.length;
      }

      return baseFilteredOptions.reduce((acc: number, product: BaseProductWithCodes) => {
        if (!product.codes.length) {
          return acc;
        }

        return product.codes.some((code) => codes.includes(code)) ? acc + 1 : acc;
      }, 0);
    };

    const result: DropdownFilterOption[] = [];

    if (allOption) {
      const itemsLength = computeItemsLength([]);
      const mergedAllOption: DropdownFilterOption = {
        value: allOption.value,
        name: allOption.name,
        codes: allOption.codes,
        itemsLength,
        label: `${allOption.name} `
      };

      result.push(mergedAllOption);
    }

    const merged = Array.from(groups.values())
      .sort((a, b) => a.order - b.order)
      .map((group) => {
        const codes = Array.from(group.codes);
        const itemsLength = computeItemsLength(codes);
        return {
          value: codes.join('|'),
          name: group.name,
          label: `${group.name}`,
          itemsLength,
          codes
        };
      })
      .filter((option) => option.itemsLength > 0);

    return [...result, ...merged];
  }, [activeTabFilterOptions, baseFilteredOptions]);

  useEffect(() => {
    if (!dropdownFilterOptions.length) {
      if (activeFilterValue) {
        setActiveFilterValue('');
      }
      return;
    }

    const isValid = dropdownFilterOptions.some((option) => option.value === activeFilterValue);

    if (!isValid) {
      const fallback =
        dropdownFilterOptions.find((option) => option.value === 'ALL') ?? dropdownFilterOptions[0];

      if (fallback && fallback.value !== activeFilterValue) {
        setActiveFilterValue(fallback.value);
      }
    }
  }, [dropdownFilterOptions, activeFilterValue]);

  const selectedFilterOption = React.useMemo(() => {
    if (!dropdownFilterOptions.length) {
      return null;
    }

    return (
      dropdownFilterOptions.find((option) => option.value === activeFilterValue) ??
      dropdownFilterOptions[0]
    );
  }, [dropdownFilterOptions, activeFilterValue]);

  const filteredOptionsForTab = React.useMemo(() => {
    if (!selectedFilterOption || selectedFilterOption.value === 'ALL') {
      return baseFilteredOptions;
    }

    if (!selectedFilterOption.codes.length) {
      return baseFilteredOptions;
    }

    return baseFilteredOptions.filter((product) => {
      if (!product.codes.length) {
        return false;
      }

      return product.codes.some((code: string) => selectedFilterOption.codes.includes(code));
    });
  }, [baseFilteredOptions, selectedFilterOption]);

  const orderedFilteredOptions = React.useMemo(() => {
    if (!filteredOptionsForTab.length) {
      return filteredOptionsForTab;
    }

    if (!dropdownFilterOptions.length) {
      return filteredOptionsForTab;
    }

    const orderMap = new Map<string, number>();

    dropdownFilterOptions.forEach((option, index) => {
      option.codes.forEach((code) => {
        if (!orderMap.has(code)) {
          orderMap.set(code, index);
        }
      });
      if (option.value === 'ALL') {
        orderMap.set('ALL', index);
      }
    });

    return [...filteredOptionsForTab].sort((a, b) => {
      const firstCodeA = a.codes.find((code) => orderMap.has(code)) ?? 'ALL';
      const firstCodeB = b.codes.find((code) => orderMap.has(code)) ?? 'ALL';

      const orderA = orderMap.get(firstCodeA) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(firstCodeB) ?? Number.MAX_SAFE_INTEGER;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      const labelA = a.raw?.content?.title ?? a.raw?.name ?? '';
      const labelB = b.raw?.content?.title ?? b.raw?.name ?? '';

      return String(labelA).localeCompare(String(labelB));
    });
  }, [filteredOptionsForTab, dropdownFilterOptions]);

  const validFilterCodes = React.useMemo(() => {
    return dropdownFilterOptions
      .filter((option) => option.codes && option.codes.length)
      .flatMap((option) => option.codes);
  }, [dropdownFilterOptions]);

  const categorySections = React.useMemo<CategorySection[]>(() => {
    if (!orderedFilteredOptions.length) {
      return [];
    }

    const primaryOptions = dropdownFilterOptions.filter((option) => option.codes.length);
    const fallbackOption = dropdownFilterOptions.find((option) => option.value === 'ALL');

    if (!primaryOptions.length && !fallbackOption) {
      return [];
    }

    const sections = primaryOptions.map((option) => ({
      option,
      items: [] as BaseProductWithCodes[]
    }));

    const fallbackItems: BaseProductWithCodes[] = [];

    orderedFilteredOptions.forEach((product) => {
      const matchingSection = sections.find((section) =>
        product.codes.some((code) => section.option.codes.includes(code))
      );

      if (matchingSection) {
        matchingSection.items.push(product);
        return;
      }

      if (fallbackOption) {
        fallbackItems.push(product);
      }
    });

    const populatedSections = sections.filter((section) => section.items.length > 0);

    if (fallbackOption && fallbackItems.length) {
      populatedSections.push({ option: fallbackOption, items: fallbackItems });
    }

    return populatedSections;
  }, [orderedFilteredOptions, dropdownFilterOptions]);

  const totalSelectedOptionsCount = React.useMemo(() => {
    if (!allowMultipleOptionsSelection) {
      return serviceOptionsOnCart.length;
    }

    // Start with cart items
    let count = serviceOptionsOnCart.length;

    // Adjust based on multipleSelections
    multipleSelections.forEach((selection) => {
      const cartItem = serviceOptionsOnCart.find((item: any) => item?.id === selection.id);

      if (selection.quantity > 0 && !cartItem) {
        // New selection not in cart yet - add the quantity
        count += selection.quantity;
      } else if (cartItem) {
        // Item is in cart
        const cartQuantity = cartItem.quantity || 1;
        if (selection.quantity === 0) {
          // Removal of cart item - subtract cart quantity
          count -= cartQuantity;
        } else if (selection.quantity !== cartQuantity) {
          // Quantity change - adjust the difference
          count += selection.quantity - cartQuantity;
        }
      }
    });

    return count;
  }, [serviceOptionsOnCart, multipleSelections, allowMultipleOptionsSelection]);

  const renderOptionsSelectedText = useCallback(() => {
    return totalSelectedOptionsCount && translations
      ? getTranslation(translations, 'options_selected')?.replace(
          '{0}',
          String(totalSelectedOptionsCount)
        )
      : null;
  }, [totalSelectedOptionsCount, translations]);

  const renderProductCard = useCallback(
    (prod: BaseProductWithCodes) => {
      const content = prod?.raw?.content;
      if (!content) {
        return null;
      }

      const categoryForCard =
        prod.codes.find((code: string) => validFilterCodes.includes(code)) || 'all';

      const productKey = content?.code ?? prod?.raw?.name;
      const offer = activeOptions?.find((service: any) => service.id === content?.code);

      const handleCardClick = (id: string, isSelected: boolean, quantity: number) => {
        if (allowMultipleOptionsSelection) {
          const itemTypeMap: Record<string, string> = {
            TV: TV_SERVICE_OPTION,
            INTERNET: INTERNET_SERVICE_OPTION,
            VOICE: VOICE_SERVICE_OPTION,
            MOBILEPOSTPAID_RATEPLAN: MOBILEPOSTPAID_SERVICE_OPTION
          };

          const itemType = itemTypeMap[activeTabKey];
          handleMultiSelection({ id, isSelected, quantity, offer, itemType });
        } else {
          emitServiceOptionSelection({
            id,
            isSelected,
            quantity,
            offer
          });
        }
      };

      return (
        <div key={productKey} className={styles['recommended-options__category-item']}>
          <ServiceOptionCard
            content={content}
            offer={offer}
            onClick={handleCardClick}
            checked={isProductInServiceOptions(productKey)}
            show
            setShowpop={() => {}}
            serviceOptions={serviceOptionsOnCart}
            category={categoryForCard}
            activeTab={activeTabKey}
            translations={translations}
            cartLoading={cartLoading}
            beneficiaryProducts={beneficiaryProducts}
          />
        </div>
      );
    },
    [
      validFilterCodes,
      serviceOptionsOnCart,
      activeTabKey,
      isProductInServiceOptions,
      translations,
      emitServiceOptionSelection
    ]
  );

  const renderProductRows = useCallback(
    (products: BaseProductWithCodes[]) => {
      if (!products.length) {
        return null;
      }

      const rows: JSX.Element[] = [];

      const itemsPerRow = viewPort === 'mobile' ? 1 : 2;
      for (let index = 0; index < products.length; index += itemsPerRow) {
        const rowItems = products.slice(index, index + itemsPerRow);
        const rowHasBadge = rowItems.some((item) => Boolean(item?.raw?.content?.badge));

        const rowKey = rowItems
          .map((item) => String(item?.raw?.content?.code ?? item?.raw?.name ?? index))
          .join('-');

        rows.push(
          <div
            key={`row-${rowKey}`}
            className={[
              styles['recommended-options__category-row'],
              rowHasBadge ? styles['recommended-options__category-row--with-badge'] : ''
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {rowItems.map((item) => renderProductCard(item))}
          </div>
        );
      }

      return rows;
    },
    [renderProductCard]
  );

  if (!content || !tabs.length) {
    return null;
  }

  return (
    <>
      <Row>
        <div
          ref={containerRef}
          className={containerClasses}
          aria-label={ariaLabel || 'Recommended options for your plan'}
        >
          <div className={styles['recommended-options__header']}>
            <div className={styles['recommended-options__text-content']}>
              <h2 className={styles['recommended-options__headline']}>{headline}</h2>
              <div className={styles['recommended-options__description']}>
                {description && Array.isArray(description) && description.length > 0
                  ? render(description)
                  : typeof description === 'string'
                    ? description
                    : null}
              </div>
            </div>
          </div>

          <div className={styles['recommended-options__content']}>
            <div
              ref={tabsRef}
              className={styles['recommended-options__tabs']}
              role='tablist'
              aria-label='Product categories'
            >
              {tabs.map((tab: RecommendedOptionsTabItem) => (
                <button
                  key={tab._uid}
                  type='button'
                  role='tab'
                  aria-selected={activeTabKey === tab.tabKey}
                  aria-controls={`tabpanel-${tab.tabKey}`}
                  id={`tab-${tab.tabKey}`}
                  className={`${styles['recommended-options__tab']} ${
                    activeTabKey === tab.tabKey ? styles['recommended-options__tab--active'] : ''
                  }`}
                  onClick={() => handleTabClick(tab.tabKey)}
                >
                  <div className={styles['recommended-options__tab-content']}>
                    <span className={styles['recommended-options__tab-label']}>{tab.tabLabel}</span>
                  </div>
                  <div className={styles['recommended-options__tab-indicator']} />
                </button>
              ))}
            </div>

            <>
              {loadingTabs[activeTabKey] || storyblokOptionsLoading ? (
                <ServiceOptionLoader />
              ) : (serviceOptionsData as any)?.[activeTabKey]?.error ||
                serviceOptionsData?.[activeTabKey]?.raw?.isAxiosError ||
                storyblokOptionsError ? (
                <div className={styles['recommended-options__error']}>
                  <p>
                    {getTranslation(translations, 'error_loading_options') ||
                      'Unable to load options. Please try again.'}
                  </p>

                  <FalconButtonV2
                    content={{ buttonLabel: getTranslation(translations, 'retry') || 'Retry' }}
                    onClick={() => {
                      if (storyblokOptionsError && typeof window !== 'undefined') {
                        window.location.reload();
                        return;
                      } else {
                        loadOptionsForTab(activeTabKey, true);
                      }
                    }}
                  />
                </div>
              ) : (
                <>
                  {/** Only show dropdown if more than 1 filter option */}
                  {dropdownFilterOptions.length > 1 && activeTabKey == 'TV' ? (
                    <div className='row col-12'>
                      <div className='col-sm-6 col-12' style={{ zIndex: 2, position: 'relative' }}>
                        <ReactInputSelectV2
                          name='filter Options'
                          options={dropdownFilterOptions}
                          optionsValue='value'
                          optionsLabel='label'
                          defaultValue={selectedFilterOption ?? null}
                          updateValue={selectedFilterOption ?? undefined}
                          content={{
                            label: getTranslation(translations, 'filter_options')
                          }}
                          onChange={(event: any) => {
                            const selected = event?.value as DropdownFilterOption | undefined;
                            if (selected?.value) {
                              setActiveFilterValue(selected.value);

                              // Track filter selection with analytics
                              try {
                                if (typeof window !== 'undefined' && window.dataLayer) {
                                  window.dataLayer.push({
                                    event: GAEVENT,
                                    event_name: APPLYFILTER,
                                    selection: selected.name || selected.value
                                  });
                                }
                              } catch (error) {
                                console.error('Analytics tracking error (apply_filter):', error);
                              }
                              return;
                            }

                            const fallback =
                              dropdownFilterOptions.find((option) => option.value === 'ALL') ??
                              dropdownFilterOptions[0];

                            setActiveFilterValue(fallback ? fallback.value : '');
                          }}
                        />
                      </div>
                      <div
                        className={`col-sm-6 col-12 ${styles['selection_options_number']} ${styles['selection_options_number_with_filter']}`}
                      >
                        {renderOptionsSelectedText()}
                      </div>
                    </div>
                  ) : serviceOptionsOnCart.length ? (
                    <div className='row col-12'>
                      <div className='col-sm-6 col-12' style={{ zIndex: 2, position: 'relative' }}>
                        &nbsp;
                      </div>
                      <div className={`col-sm-6 col-12 ${styles['selection_options_number']}`}>
                        {renderOptionsSelectedText()}
                      </div>
                    </div>
                  ) : null}
                  <div
                    id={`tabpanel-${activeTabKey}`}
                    role='tabpanel'
                    aria-labelledby={`tab-${activeTabKey}`}
                    className={`${styles['recommended-options__options-grid']} 
                 ${
                   normalizeHeight(
                     viewPort === 'mobile' ? content?.listHeightMobile : content?.listHeightDesktop
                   )
                     ? styles['recommended-options__options-grid-with-scroll']
                     : ''
                 }`}
                    style={(() => {
                      const height = normalizeHeight(
                        viewPort === 'mobile'
                          ? content?.listHeightMobile
                          : content?.listHeightDesktop
                      );
                      return height ? { height } : undefined;
                    })()}
                  >
                    {categorySections.length &&
                    (!selectedFilterOption || selectedFilterOption.value === 'ALL') ? (
                      categorySections.map((section) => (
                        <div
                          key={section.option.value}
                          className={styles['recommended-options__category-section']}
                        >
                          <h3 className={styles['recommended-options__category-heading']}>
                            {section.option.name}
                          </h3>
                          <div className={styles['recommended-options__category-items']}>
                            {renderProductRows(section.items)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={styles['recommended-options__category-items']}>
                        {renderProductRows(orderedFilteredOptions)}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          </div>
          {allowMultipleOptionsSelection && (
            <div className='d-flex justify-content-end w-100'>
              <button
                disabled={cartLoading || isPartialFetching || multipleSelections.length === 0}
                className='primarybutton w-auto'
                onClick={() => emitServiceOptionSelection(multipleSelections)}
              >
                {content?.continueButtonLabel || 'Continue'}
              </button>
            </div>
          )}
        </div>
      </Row>
    </>
  );
};

export default RecommendedOptions;
